import { fetchEventPage, type EventPage } from './fetchEventPage'

/**
 * Query params for the membership events list.
 *
 * Deliberately the SAME shape `/games/follow` takes — the schedule screens
 * build one filter object and send it to both, so games and events can never
 * end up bounded differently. Every field is optional: an absent key means "no
 * filter", which is not the same as sending an empty one.
 */
export interface ListMembershipEventsQuery {
  schedule_id?: string
  team_id?: string
  organization_id?: string
  venue_id?: string
  facility_id?: string
  /** Include descendants of the selected entity, as the games call does. */
  recursive?: number
  /**
   * NOT accepted here. `/games/follow` takes `membership_type`; this endpoint
   * does not, so the caller strips it — see `useMembershipEventStream`. Left
   * documented rather than silently absent, since the two query shapes are
   * otherwise identical and the difference is easy to reintroduce by accident.
   */
  starts_after?: string
  starts_before?: string
  order?: string
  per_page?: number
  /** Mobile's Schedule tab filters by sport; web has no such control. */
  sport_id?: string
  generic_sport_id?: string
  /** Free-text search, mobile only. */
  query?: string
}

/**
 * ONE page of the events the current user has a membership claim on, across
 * every team they follow — the event-side counterpart of `/games/follow`.
 *
 * Backs the viewer's own schedule, where membership scoping is exactly the
 * point. A schedule's full event list — which answers for everyone, not just
 * the viewer — is `listScheduleEventsPage`.
 *
 * Errors throw, like every other event service. An earlier version swallowed
 * 404/501 while the endpoint was still rolling out; now that it is deployed, a
 * missing endpoint is a real fault and should surface as one rather than
 * quietly reading as "no events".
 */
export const listMembershipEventsPage = (
  query: ListMembershipEventsQuery = {},
  page = 1
): Promise<EventPage> => fetchEventPage('/events/membership', query, page)
